import type { AgentSkill } from '@paguz/contracts';
import {
  Badge,
  Button,
  cn,
  inputBaseClass,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { ArrowLeftIcon, GitCompareIcon, PlusIcon, ZapIcon } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog } from '../../components/confirm-dialog.tsx';
import { Field, Textarea, compactInputClass } from '../../components/form-controls.tsx';
import { DiffView, Markdown } from '../../components/markdown.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { PHASE_LABEL } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { AGENT_LABEL } from '../agents/labels.ts';

/** Skills de los agentes con versión activa (`06-backoffice.md` §4.9). */
export function SkillsPage() {
  const query = useQuery({ queryKey: queryKeys.skills, queryFn: api.listSkills });
  return (
    <>
      <PageHeader
        title="Skills"
        description="Instrucciones especializadas que el agente carga según la fase de originación o la intención del cliente. Cada cambio es una versión; solo una está activa."
      />
      <Panel bodyClassName="p-0">
        {query.isPending ? (
          <div className="p-5">
            <LoadingRows />
          </div>
        ) : query.isError ? (
          <div className="p-5">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead>Agentes</TableHead>
                <TableHead>Se carga en</TableHead>
                <TableHead className="text-center">Activa</TableHead>
                <TableHead className="text-center">Última</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.data.map((s) => (
                <TableRow key={s.code}>
                  <TableCell>
                    <Link
                      to="/skills/$code"
                      params={{ code: s.code }}
                      className="font-semibold text-primary hover:underline"
                    >
                      {s.title}
                    </Link>
                    <div className="font-mono text-xs text-muted-foreground">{s.code}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {s.roles.map((r) => AGENT_LABEL[r]).join(', ')}
                  </TableCell>
                  <TableCell className="text-sm">{loadsIn(s)}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="success">v{s.activeVersion ?? '—'}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={s.latestVersion !== s.activeVersion ? 'warning' : 'outline'}>
                      v{s.latestVersion}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </>
  );
}

function loadsIn(s: AgentSkill): string {
  const phases = s.phases.map((p) => PHASE_LABEL[p] ?? p);
  const intents = s.intents.map((i) => `intención ${i}`);
  return [...phases, ...intents].join(' · ') || 'Siempre';
}

const route = getRouteApi('/app/skills/$code');

export function SkillPage() {
  const { code } = route.useParams();
  const query = useQuery({ queryKey: queryKeys.skill(code), queryFn: () => api.getSkill(code) });
  return (
    <>
      <Link
        to="/skills"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Skills
      </Link>
      {query.isPending ? (
        <LoadingRows rows={8} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <SkillEditor
          key={`${query.data.code}-${query.data.latestVersion}-${query.data.activeVersion ?? 0}`}
          skill={query.data}
        />
      )}
    </>
  );
}

function SkillEditor({ skill }: { skill: AgentSkill }) {
  const canEdit = useCan('skills:edit');
  const canActivate = useCan('skills:activate');
  const queryClient = useQueryClient();
  const versions = useQuery({
    queryKey: queryKeys.skillVersions(skill.code),
    queryFn: () => api.listSkillVersions(skill.code),
    retry: false,
  });
  const [content, setContent] = useState(skill.contentMd);
  const [note, setNote] = useState('');
  const [compare, setCompare] = useState<number | null>(null);
  const [activating, setActivating] = useState<number | null>(null);
  const refresh = (next: AgentSkill) => {
    queryClient.setQueryData(queryKeys.skill(skill.code), next);
    void queryClient.invalidateQueries({ queryKey: queryKeys.skills });
    void queryClient.invalidateQueries({ queryKey: queryKeys.skillVersions(skill.code) });
  };
  const create = useMutation({
    mutationFn: () =>
      api.createSkillVersion(skill.code, {
        contentMd: content,
        ...(note.trim() && { changeNote: note.trim() }),
      }),
    onSuccess: (next) => {
      refresh(next);
      toast.success(`Versión ${next.latestVersion} creada (aún no activa)`);
    },
    onError: (e) => toast.error(describeError(e)),
  });
  const activate = useMutation({
    mutationFn: (version: number) => api.activateSkill(skill.code, version),
    onSuccess: (next) => {
      refresh(next);
      toast.success(`Versión ${next.activeVersion ?? ''} activa: los agentes la cargan desde ya`);
    },
  });
  const list = versions.data?.data ?? [];
  const active = list.find((x) => x.version === skill.activeVersion);
  const other = list.find((x) => x.version === compare);

  return (
    <>
      <PageHeader
        title={skill.title}
        description={`${skill.roles.map((r) => AGENT_LABEL[r]).join(', ')} · ${loadsIn(skill)}`}
        actions={
          <Badge variant="success" className="text-sm">
            Activa v{skill.activeVersion ?? '—'}
          </Badge>
        }
      />
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title="Nueva versión"
          description="Parte de la versión activa. Crear no la activa: actívala después de revisarla."
          actions={
            canEdit && (
              <Button
                variant="outline"
                disabled={content === skill.contentMd || create.isPending}
                onClick={() => {
                  create.mutate();
                }}
              >
                <PlusIcon /> Crear versión
              </Button>
            )
          }
        >
          <fieldset disabled={!canEdit} className="flex flex-col gap-4">
            <Textarea
              aria-label="Contenido de la skill"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
              }}
              className="min-h-72 font-mono text-xs"
            />
            <Field label="Nota del cambio" htmlFor="sk-note">
              <input
                id="sk-note"
                className={cn(inputBaseClass, compactInputClass)}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
              />
            </Field>
          </fieldset>
        </Panel>
        <Panel title="Versión activa">
          <Markdown source={skill.contentMd} />
        </Panel>
      </div>
      <Panel
        className="mt-6"
        title="Versiones"
        description="Compara contra la activa y activa con doble confirmación."
      >
        {versions.isPending ? (
          <LoadingRows rows={3} />
        ) : versions.isError ? (
          <p className="text-sm text-muted-foreground">
            La API aún no expone el historial de versiones de skills; solo la activa (v
            {skill.activeVersion ?? '—'}) y la última (v{skill.latestVersion}).
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <ul className="flex flex-col gap-2">
              {list.map((ver) => (
                <li key={ver.version} className="flex flex-wrap items-center gap-3 text-sm">
                  <Badge variant={ver.version === skill.activeVersion ? 'success' : 'outline'}>
                    v{ver.version}
                    {ver.version === skill.activeVersion && ' · activa'}
                  </Badge>
                  <span className="text-muted-foreground">
                    {formatDateTime(ver.createdAt)} · {ver.createdBy}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{ver.changeNote ?? '—'}</span>
                  {ver.version !== skill.activeVersion && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setCompare(compare === ver.version ? null : ver.version);
                        }}
                      >
                        <GitCompareIcon /> Comparar
                      </Button>
                      {canActivate && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setActivating(ver.version);
                          }}
                        >
                          <ZapIcon /> Activar
                        </Button>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
            {other && active && (
              <DiffView
                before={active.contentMd}
                after={other.contentMd}
                labels={[`v${active.version} (activa)`, `v${other.version}`]}
              />
            )}
          </div>
        )}
      </Panel>
      <ConfirmActionDialog
        open={activating !== null}
        onOpenChange={(o) => {
          if (!o) setActivating(null);
        }}
        title={`Activar la versión ${activating ?? ''} de ${skill.title}`}
        description="Las sesiones nuevas cargan esta versión; cada respuesta registra qué versión usó."
        confirmLabel="Activar versión"
        requireReason={false}
        onConfirm={async () => {
          if (activating !== null) await activate.mutateAsync(activating);
        }}
      />
    </>
  );
}
